import React from 'react';
import {Modal} from '@/ui-kits';
import {Icon, Text} from '@/ui-kits';

interface ReturnPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ReturnPolicyModal: React.FC<ReturnPolicyModalProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      className="relative bg-white items-start py-3 sm:py-5 xs:py-6 md:py-8 px-3 sm:px-4 xs:px-6 md:px-8 lg:px-12 rounded-xl gap-4 box-border w-[95vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] xl:w-[75vw] 2xl:w-[70vw] max-w-[1200px] max-h-[90vh] overflow-y-auto mx-auto">
      <button
        className="absolute top-2 right-2 sm:top-3 sm:right-3 md:top-4 md:right-4 z-10"
        onClick={onClose}
        aria-label="Close">
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center hover:bg-ui_input_border_dark/20 transition-colors">
          <Icon type="close" className="size-4 sm:size-5 md:size-6 text-ui_blue" />
        </div>
      </button>

      <div className="w-full pr-6 sm:pr-8 md:pr-10">
        <Text variant="heading" className="font-FigtreeBold text-ui_blue mb-2 !text-xl sm:!text-2xl md:!text-3xl lg:!text-4xl">
          Return Policy
        </Text>
        <Text variant="free" className="text-ui_blue !font-FigtreeRegular mb-3 sm:mb-4 md:mb-6 !text-xs sm:!text-sm md:!text-base">
          Last Updated: 04 Feburary 2026
        </Text>
        <hr className="w-full border-t-2 border-ui_input_border mb-4 sm:mb-6" />

        <Text variant="body" className="text-deep_blue !font-FigtreeMedium !text-xs sm:!text-sm md:!text-base leading-relaxed mb-4 sm:mb-6">
          This Return Policy outlines the conditions under which a patient may receive a refund for a consultation purchased on the Opinion Module.
        </Text>

        <div className="flex flex-col gap-3 sm:gap-4 md:gap-6 lg:gap-8">
          {/* Row 1: Sections 1 and 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-16 xl:gap-20 items-start">
            {/* Section 1 */}
            <div>
              <Text variant='headingSemiBold' className="!text-base sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue mb-2 sm:mb-3">
                1. Eligible Returns
              </Text>
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed mb-2 sm:mb-3">
                This Return Policy outlines the conditions under which a patient may receive a refund for a consultation purchased on the Opinion Module.
              </Text>
              <ul className="list-disc list-outside ml-3 sm:ml-4 md:ml-5 lg:ml-6 space-y-1 sm:space-y-2">
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">The Platform provides access to medical second opinions</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">It does not provide emergency or urgent medical care</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">AI-generated summaries are informational and assistive only</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Final opinions are provided by licensed medical specialists</li>
              </ul>
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed mt-2 sm:mt-3">
                If these conditions are met, the patient is eligible for a full return.
              </Text>
            </div>

            {/* Section 2 */}
            <div>
              <Text variant='headingSemiBold' className="!text-base sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue mb-2 sm:mb-3">
                2. Non-Returnable Consultations
              </Text>
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed mb-2 sm:mb-3">
                All consultations are non-returnable once the specialist has started reviewing the medical records or once a medical opinion has been delivered.
              </Text>
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed mb-2">
                No returns will be provided in any other circumstances, including but not limited to:
              </Text>
              <ul className="list-disc list-outside ml-3 sm:ml-4 md:ml-5 lg:ml-6 space-y-1 sm:space-y-2">
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Change of mind</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Dissatisfaction with the opinion</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Delays caused by patient actions</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Incomplete or incorrect medical information</li>
              </ul>
            </div>
          </div>

          {/* Row 2: Sections 3 and 4 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-16 xl:gap-20 items-start">
            {/* Section 3 */}
            <div>
              <Text variant='headingSemiBold' className="!text-base sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue mb-2 sm:mb-3">
                3. Return Processing
              </Text>
              <ul className="list-disc list-outside ml-3 sm:ml-4 md:ml-5 lg:ml-6 space-y-1 sm:space-y-2">
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">All returns are processed through Stripe</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Returns are issued to the original payment method</li>
                <li className="text-ui_input_border_dark font-FigtreeRegular text-xs sm:text-sm md:text-base leading-relaxed">Processing timelines depend on Stripe and the patient's bank</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div>
              <Text variant='headingSemiBold' className="!text-base sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue mb-2 sm:mb-3">
                4. Finality of Decisions
              </Text>
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed">
                All return decisions made under this policy are final, except where required by applicable law.
              </Text>
            </div>
          </div>

          {/* Row 3: Section 5 (left side only) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 md:gap-10 lg:gap-16 xl:gap-20 items-start">
            {/* Section 5 */}
            <div>
              <Text variant='headingSemiBold' className="!text-base sm:!text-lg md:!text-xl lg:!text-2xl text-deep_blue mb-2 sm:mb-3">
                5. Policy Updates
              </Text>
              <Text variant="body" className="text-ui_input_border_dark !font-FigtreeRegular !text-xs sm:!text-sm md:!text-base leading-relaxed">
                This Return Policy may be updated periodically. Continued use of the platform constitutes acceptance of the updated policy.
              </Text>
            </div>
            <div></div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

